import { CircleAlert, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/shadcn/utils';

interface IntegrationRowProps {
  icon: LucideIcon;
  name: string;
  /** Beside the name: an `IntegrationStatus`, or a plan pill when locked. */
  status: ReactNode;
  /** The part of Finlay that depends on this integration. */
  usedBy?: string;
  description: ReactNode;
  /** A warning under the description, e.g. what waits on this integration. */
  note?: ReactNode;
  action?: ReactNode;
  /** Settings or forms under the row, aligned with the description. */
  children?: ReactNode;
}

/** One `<li>` of the Integrations page: icon, name, status, what uses it, one action. */
export function IntegrationRow({
  icon: Icon,
  name,
  status,
  usedBy,
  description,
  note,
  action,
  children,
}: IntegrationRowProps) {
  return (
    <li className="flex flex-col gap-4 py-5">
      <div className="grid grid-cols-[24px_minmax(0,1fr)] gap-3 md:grid-cols-[24px_minmax(0,1fr)_auto]">
        <Icon aria-hidden className="text-muted-foreground mt-0.5 size-5" />
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <span className="text-sm font-medium">{name}</span>
            {status}
            {usedBy && (
              <span className="bg-muted text-muted-foreground inline-flex h-5 items-center rounded-full px-2 text-[11px] font-medium">
                {usedBy}
              </span>
            )}
          </div>
          <p className="text-muted-foreground max-w-[480px] text-[13px] leading-[19px]">
            {description}
          </p>
          {note && (
            <p className="flex items-start gap-1.5 pt-1 text-[13px] leading-[19px] font-medium text-amber-700">
              <CircleAlert aria-hidden className="mt-0.5 size-3.5 shrink-0" />
              {note}
            </p>
          )}
        </div>
        {action && <div className="col-start-2 md:col-start-3">{action}</div>}
      </div>
      {children && <div className="pl-9">{children}</div>}
    </li>
  );
}

interface IntegrationStatusProps {
  isActive: boolean;
  children: ReactNode;
}

/** A green dot when the integration works, grey otherwise, with its label. */
export function IntegrationStatus({
  isActive,
  children,
}: IntegrationStatusProps) {
  return (
    <span className="text-muted-foreground inline-flex h-5 items-center gap-1.5 text-xs font-medium">
      <span
        className={cn(
          'size-1.5 rounded-full',
          isActive ? 'bg-semantic-560' : 'bg-neutral-500',
        )}
      />
      {children}
    </span>
  );
}
